import { useCallback, useEffect, useMemo, useState } from "react";
import {
  enablePushNotificationsFromUserGesture,
  fetchPushServerEnabled,
  pushNotificationsSupported,
} from "../lib/push";

type UsePushBannerArgs = {
  authenticated: boolean;
  hasIdentity: boolean;
  identityKey: string | null;
};

export function usePushBanner({ authenticated, hasIdentity, identityKey }: UsePushBannerArgs) {
  const [pushServerOk, setPushServerOk] = useState<boolean | null>(null);
  const [pushBannerError, setPushBannerError] = useState<string | null>(null);
  const [pushBannerDismissed, setPushBannerDismissed] = useState(() => {
    try {
      return localStorage.getItem("styx_push_banner_dismissed") === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!authenticated || !hasIdentity) {
      setPushServerOk(null);
      return;
    }
    void fetchPushServerEnabled().then(setPushServerOk);
  }, [authenticated, hasIdentity, identityKey]);

  const showPushBanner = useMemo(() => {
    if (!authenticated || !hasIdentity) return false;
    if (pushServerOk !== true) return false;
    if (pushBannerDismissed) return false;
    if (!pushNotificationsSupported()) return false;
    return Notification.permission === "default";
  }, [authenticated, hasIdentity, pushBannerDismissed, pushServerOk]);

  const onEnablePushTap = useCallback(() => {
    setPushBannerError(null);
    void enablePushNotificationsFromUserGesture().then((r) => {
      if (r.ok) {
        try {
          localStorage.setItem("styx_push_banner_dismissed", "1");
        } catch {
          /* ignore */
        }
        setPushBannerDismissed(true);
      } else {
        setPushBannerError(r.message ?? null);
      }
    });
  }, []);

  const onDismissPushBanner = useCallback(() => {
    try {
      localStorage.setItem("styx_push_banner_dismissed", "1");
    } catch {
      /* ignore */
    }
    setPushBannerDismissed(true);
  }, []);

  return {
    showPushBanner,
    pushBannerError,
    onEnablePushTap,
    onDismissPushBanner,
  };
}
